import { makeStyles } from '@material-ui/styles'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useReactRouter from 'use-react-router'
import ApiClient from 'api-client/ApiClient'
import {
  unableCreateVirtualizedClusterDoc,
  vmhaClusterHostRequirementsDoc,
  vmhaDoc,
} from 'app/docLinks'
import Alert from 'core/components/Alert'
import ExternalLink from 'core/components/ExternalLink'
import LearnMoreLink from 'core/components/LearnMoreLink'
import SelectableCard from 'core/components/SelectableCard'
import SimpleLink from 'core/components/SimpleLink'
import PicklistField from 'core/components/validatedForm/DropdownField'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import type { Route } from 'core/plugins/route'
import type Theme from 'core/themes/model'
import { customValidator } from 'core/utils/fieldValidators'
import { routeManager } from 'core/utils/routes'
import { trackEvent } from 'utils/tracking'
import { IHealthServiceDetailsPageTabs } from '../../networks/model'
import { createClusterHostGroup } from './actions'
import CapacityReserveCard, {
  DEFAULT_PERCENT as DEFAULT_CAPACITY_RESERVE_PERCENT,
  MAX_PERCENT as MAX_CAPACITY_RESERVE_PERCENT,
  MIN_PERCENT as MIN_CAPACITY_RESERVE_PERCENT,
} from './CapacityReserveCard'
import ClusterAccessField from './ClusterAccessField'
import CpuModePicklist from './CpuModePicklist'
import EvacuationSpeedSlider from './EvacuationSpeedSlider'
import { getVMHAConditions, mapCpuModeToBody } from './helpers'
import {
  type CreateClusterHostGroup,
  EvacuationSpeed,
  RebalancingFrequency,
  RebalancingStrategy,
  type VmhaConfigBody,
} from './model'
import RebalancingFrequencyPickList from './RebalancingFrequencyPickList'
import VmhaConditionsStatus from './VmhaConditionsStatus'

const gpuModes = [
  { label: 'Passthrough', value: 'passthrough' },
  { label: 'vGPU', value: 'vgpu' },
]

const nameValidator = customValidator((value) => {
  if (!value) {
    return true
  }
  const regex = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?(\\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*$/
  return value.match(regex)
}, `Cluster name must consist of lower case alphanumeric characters or '-', start with an alphabetic character, and end with an alphanumeric character (e.g. 'my-name', or 'abc-123')`)

const { nova, neutron, haMgr } = ApiClient.getInstance()

export const useStyles = makeStyles<Theme>((theme) => ({
  header: {
    display: 'grid',
    gridTemplateColumns: '1fr 21fr',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(4),
  },
  toggleSwitch: {
    marginTop: theme.spacing(2),
  },
  vmhaToggleSwitchWarning: {
    marginLeft: theme.spacing(7),
  },
  customFormFieldSection: {
    '& .content': {
      marginLeft: theme.spacing(0),
    },
  },
  customLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  customLabelTitle: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
  questionCircle: {
    marginLeft: theme.spacing(1),
  },
  rebalancingFrequency: {
    marginLeft: theme.spacing(7),
  },
  gpuCards: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 8,
  },
  card: {
    padding: 8,
  },
  gpuFields: {
    marginLeft: theme.spacing(7),
    display: 'grid',
    gap: 16,
  },
  textContainer: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  vmhaModal: {
    '& .progress-root': {
      minHeight: 'auto',
    },
  },
  vmhaRiskModalText: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  excludeEphemeralVmsToggle: {
    marginLeft: theme.spacing(7),
    display: 'grid',
    gridFlow: 'row',
    gap: theme.spacing(2),
  },
  tenantAccessField: {
    marginLeft: theme.spacing(7),
  },
}))

interface Props {
  addRoute: Route
  existingClusterHostGroups: any[]
  existingHostAggregates: any[]
  reload: () => void
}

export default function CreateClusterHostGroupsModal({
  addRoute,
  existingClusterHostGroups,
  existingHostAggregates,
  reload,
}: Props) {
  const classes = useStyles()
  const { history } = useReactRouter()
  const [healthCheckFailed, setHealthCheckFailed] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [vmhaConfigError, setVmhaConfigError] = useState<string | null>(null)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [capacityReserveError, setCapacityReserveError] = useState<string | null>(null)
  const defaultParams = {
    name: '',
    vmHighAvailability: false,
    autoResourceRebalancing: false,
    enableGpu: false,
    rebalancingFrequency: RebalancingFrequency.Default,
    gpuType: 'passthrough',
    cpuMode: 'default',
    cpuModel: '',
    evacuationSpeed: EvacuationSpeed.Conservative,
    nfsHeartbeatPath: '',
    storageHeartbeatEnabled: false,
    excludeEphemeralVms: false,
    shared: true,
    tenantIds: [] as string[],
    capacityReserveEnabled: false,
    capacityReservePercent: DEFAULT_CAPACITY_RESERVE_PERCENT,
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)
  const { update, updating, error, reset } = useUpdateAction(createClusterHostGroup)

  const existingClusterAndHostAggregateNames = useMemo(() => {
    return new Set([
      ...existingClusterHostGroups?.map((cluster) => cluster.name),
      ...existingHostAggregates?.map((hostAggregate) => hostAggregate.name),
    ])
  }, [existingClusterHostGroups, existingHostAggregates])

  // PCD-4129: require a valid MIN_CAPACITY_RESERVE_PERCENT..MAX_CAPACITY_RESERVE_PERCENT
  // percent whenever the capacity reserve toggle is on, mirrored from
  // EditClusterHostGroupModal.
  useEffect(() => {
    if (!params.capacityReserveEnabled) {
      setCapacityReserveError(null)
      return
    }
    const value = Number(params.capacityReservePercent)
    if (
      Number.isNaN(value) ||
      value < MIN_CAPACITY_RESERVE_PERCENT ||
      value > MAX_CAPACITY_RESERVE_PERCENT
    ) {
      setCapacityReserveError(
        `Enter a reserved capacity percentage between ${MIN_CAPACITY_RESERVE_PERCENT} and ${MAX_CAPACITY_RESERVE_PERCENT}.`,
      )
    } else {
      setCapacityReserveError(null)
    }
  }, [params.capacityReserveEnabled, params.capacityReservePercent])

  const uniqueNameValidator = useCallback(
    () =>
      customValidator((value) => {
        return !existingClusterAndHostAggregateNames?.has(value)
      }, 'A cluster or host aggregate with this name already exists. Please use a different name.'),
    [existingClusterAndHostAggregateNames],
  )

  const handleClose = useCallback(() => {
    setParams(defaultParams)
    setHealthCheckFailed(null)
    setVmhaConfigError(null)
    setSubmitAttempted(false)
    reset()
    history.push(routeManager.routes.openstack.clusters.path())
  }, [setParams, reset, history])

  const checkServicesHealth = async () => {
    setHealthCheckFailed(null)
    let novaOK = false
    try {
      await nova.checkHealth()
      novaOK = true
      await neutron.checkHealth()
      return true
    } catch (e) {
      setHealthCheckFailed({
        title: 'Cluster creation failed',
        message: (
          <Text variant="body2">
            {!novaOK ? 'Compute' : 'Networking'} service is currently unhealthy. Please check the{' '}
            <SimpleLink
              src={routeManager.routes.openstack.health.details.path({
                tab: IHealthServiceDetailsPageTabs.ControlPlaneServicesHealth,
              })}
            >
              <strong>Service Health Dashboard</strong>
            </SimpleLink>{' '}
            and try again in sometime once the service is healthy.{' '}
            <ExternalLink url={unableCreateVirtualizedClusterDoc}>
              <strong>More Info</strong>
            </ExternalLink>
          </Text>
        ),
      })
      return false
    }
  }

  const submitForm = useCallback(async () => {
    setSubmitAttempted(true)
    // CCHGM-FR-016: block submit when dedicating to tenants but none are selected
    if (!params.shared && params.tenantIds.length === 0) {
      return
    }
    setSubmitting(true)
    const heathCheckOk = await checkServicesHealth()
    if (!heathCheckOk) {
      setSubmitting(false)
      return
    }

    const body: CreateClusterHostGroup = {
      name: params.name,
      vmHighAvailability: { enabled: params.vmHighAvailability },
      autoResourceRebalancing: {
        enabled: params.autoResourceRebalancing,
        ...(params.autoResourceRebalancing && {
          rebalancingStrategy: RebalancingStrategy.VMWorkloadConsolidation,
          rebalancingFrequencyMins: params.rebalancingFrequency,
        }),
      },
      gpu: {
        enabled: params.enableGpu,
        mode: params.gpuType,
      },
      cpu: mapCpuModeToBody(params.cpuMode, params.cpuModel),
      // CCHGM-FR-014: dedicate to specific tenants, or omit entirely for Shared
      ...(!params.shared && { tenants: params.tenantIds }),
    }
    const { success } = await update({ body })
    if (success) {
      // PCD-4129: pushed unconditionally right after the resmgr create call
      // that sets vmHighAvailability.enabled -- see EditClusterHostGroupModal
      // for the full rationale. Best-effort: failure doesn't block creation.
      try {
        await haMgr.setVMHAEnabled(params.name, params.vmHighAvailability)
      } catch {
        // Non-fatal -- see comment above.
      }

      // CCHGM-FR-004, CCHGM-FR-005, CCHGM-FR-006: post VMHA config only when VMHA is enabled
      if (params.vmHighAvailability) {
        const vmhaConfigBody: VmhaConfigBody = {
          mode: params.evacuationSpeed,
          exclude_ephemeral_vms: params.excludeEphemeralVms,
          ...(params.storageHeartbeatEnabled &&
            params.nfsHeartbeatPath.trim() && {
              host_config: { nfs_heartbeat_path: params.nfsHeartbeatPath.trim() },
            }),
        }
        try {
          await haMgr.updateVmhaConfig(params.name, vmhaConfigBody)
        } catch {
          // CCHGM-FR-007: surface HaMgr error, keep modal open
          setVmhaConfigError(
            'Cluster was created but VMHA configuration could not be saved. Please edit the cluster to retry.',
          )
          setSubmitting(false)
          return
        }

        // PCD-4129: only push the capacity reserve when it's been enabled
        // -- there's nothing to save for a brand-new cluster otherwise.
        if (params.capacityReserveEnabled) {
          try {
            await haMgr.updateAllocationThreshold(params.name, {
              allocation_threshold_percent: params.capacityReservePercent,
              capacity_reserve_enabled: params.capacityReserveEnabled,
            })
          } catch {
            setVmhaConfigError(
              'Cluster was created but the failover capacity reserve could not be saved. Please edit the cluster to retry.',
            )
            setSubmitting(false)
            return
          }
        }
      }
      trackEvent(`Add Cluster - Finished`)
      reload()
      handleClose()
    }
    setSubmitting(false)
  }, [params, update, handleClose])

  return (
    <ModalForm
      route={addRoute}
      title={`Add Cluster`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating || submitting}
      error={
        error ||
        (vmhaConfigError && { title: 'VMHA Configuration Failed', message: vmhaConfigError }) ||
        healthCheckFailed
      }
      submitTitle={`Add Cluster`}
      disableSubmit={!!capacityReserveError}
    >
      {healthCheckFailed && (
        <>
          <Alert variant="error" title={healthCheckFailed?.title}>
            <Text variant="body2">{healthCheckFailed?.message}</Text>
          </Alert>
          <br />
        </>
      )}
      {vmhaConfigError && (
        <>
          <Alert variant="error" title="VMHA Configuration Failed">
            <Text variant="body2">{vmhaConfigError}</Text>
          </Alert>
          <br />
        </>
      )}
      <div className={classes.header}>
        <img
          alt="host group"
          src="/ui/images/host-group.svg"
          data-testid="create-cluster-host-groups-modal--img-0"
        />
        <Text variant="body2">
          Create a new cluster to organize and manage hosts for workload deployment.
        </Text>
      </div>
      <FormFieldSection title="Cluster Configuration" className={classes.customFormFieldSection}>
        <TextField
          id="name"
          label="Name"
          onChange={getParamsUpdater('name')}
          value={params.name}
          validations={[nameValidator, uniqueNameValidator()]} // Pass the validator function
          info={`Cluster name must consist of lower case alphanumeric characters or '-', start with an alphabetic character, and end with an alphanumeric character (e.g. 'my-name', or 'abc-123')`}
          required
        />
        <PicklistField
          DropdownComponent={CpuModePicklist}
          id="cpuMode"
          onChange={getParamsUpdater('cpuMode')}
          value={params.cpuMode}
          label="CPU Mode"
          info={
            <div>
              <div>Controls how the hypervisor exposes CPU features to VMs.</div>
              <br />
              <div>Default: Select the latest supported CPU model on the host.</div>
              <br />
              <div>
                Custom: Specify a standardized CPU model to enable live migration between hosts with
                different physical CPUs.
              </div>
              <br />
              <div>
                Host Model: Closely matches the physical CPU for balanced performance and migration
                flexibility.
              </div>
              <br />
              <div>
                Host Passthrough: Directly exposes the physical CPU for maximum performance but
                limits migration to identical hardware.
              </div>
            </div>
          }
        />
        {params.cpuMode === 'custom' && (
          <TextField
            id="cpuModel"
            label="CPU Model"
            onChange={getParamsUpdater('cpuModel')}
            value={params.cpuModel}
            placeholder="eg. Nehalem"
            required
            info="The CPU model that the virtualization layer (hypervisor) exposes to virtual machines (VMs). Used to standardize CPU features across hosts on this cluster to enable live migration between hosts. This may differ from the physical host CPU model."
          />
        )}
        <div className={classes.toggleSwitch}>
          <ToggleSwitchField
            id="vmHighAvailability"
            label="VM High Availability"
            value={params.vmHighAvailability}
            onChange={(value) => getParamsUpdater('vmHighAvailability')(value)}
            customLabel={
              <div className={classes.customLabel}>
                <Text variant="caption1" className={classes.customLabelTitle}>
                  VM High Availability
                  <HintTooltip message="Automatically detect host failure and recover VMs on other host group hosts." />
                </Text>
                <Text variant="body2" style={{ marginBottom: 8 }}>
                  VM High Availability can be enabled for this cluster. The cluster will reach
                  'Protected' status only when all prerequisites below are satisfied. Partial or
                  unmet conditions will result in 'Degraded' or 'Not Protected' states.{' '}
                  <ExternalLink
                    variant="caption2"
                    url={vmhaClusterHostRequirementsDoc}
                    icon="external-link"
                  >
                    <b>More Info</b>
                  </ExternalLink>
                </Text>
                <VmhaConditionsStatus
                  title=""
                  conditions={getVMHAConditions(false)}
                  textWithPointers
                />
              </div>
            }
          />
        </div>
        {/* CCHGM-FR-010: exclude-ephemeral-VMs toggle, below the VMHA prereqs, only while VMHA is ON */}
        {params.vmHighAvailability && (
          <>
            <div className={classes.excludeEphemeralVmsToggle}>
              <ToggleSwitchField
                id="excludeEphemeralVms"
                label="Exclude all ephemeral VMs from evacuations"
                value={params.excludeEphemeralVms}
                onChange={(value) => getParamsUpdater('excludeEphemeralVms')(value)}
                customLabel={
                  <div className={classes.customLabel}>
                    <Text variant="caption1" className={classes.customLabelTitle}>
                      Exclude all ephemeral VMs from evacuations
                    </Text>
                    <Text variant="body2">
                      Enabling the toggle will exclude all ephemeral VMs (those with local storage
                      and shared storage).
                    </Text>
                  </div>
                }
              />
              {/* CCHGM-FR-011: link to VM HA docs, alongside the exclude-ephemeral-VMs toggle */}
              <LearnMoreLink url={vmhaDoc} text="Learn More about VM HA" />
            </div>
            {/* PCD-4129: capacity reserve, above the evacuation speed panel */}
            <CapacityReserveCard
              enabled={params.capacityReserveEnabled}
              onEnabledChange={(value) => getParamsUpdater('capacityReserveEnabled')(value)}
              percent={params.capacityReservePercent}
              onPercentChange={getParamsUpdater('capacityReservePercent')}
              className={classes.rebalancingFrequency}
            />
            {/* CCHGM-FR-001, CCHGM-FR-002, CCHGM-FR-003: show VMHA config fields when VMHA is ON */}
            <EvacuationSpeedSlider
              value={params.evacuationSpeed}
              onChange={(value) => getParamsUpdater('evacuationSpeed')(value)}
              className={classes.rebalancingFrequency}
              params={params}
              getParamsUpdater={getParamsUpdater}
            />
          </>
        )}
        <div className={classes.toggleSwitch}>
          <ToggleSwitchField
            id="autoResourceRebalancing"
            label="Dynamic Resource Rebalancing"
            value={params.autoResourceRebalancing}
            onChange={(value) => getParamsUpdater('autoResourceRebalancing')(value)}
            customLabel={
              <div className={classes.customLabel}>
                <Text variant="caption1" className={classes.customLabelTitle}>
                  Dynamic Resource Rebalancing
                  <HintTooltip message="Dynamically rebalances workloads across hosts in the host group to optimize resource utilization and prevent overload." />
                </Text>
                <Text variant="body2">
                  Dynamically rebalance workloads across hosts in the cluster.
                </Text>
              </div>
            }
          />
        </div>
        {params.autoResourceRebalancing && (
          <RebalancingFrequencyPickList
            value={params.rebalancingFrequency}
            onChange={(value) => getParamsUpdater('rebalancingFrequency')(value)}
            className={classes.rebalancingFrequency}
          />
        )}
        <div className={classes.toggleSwitch}>
          <ToggleSwitchField
            id="enableGpu"
            label="GPU"
            value={params.enableGpu}
            onChange={(value) => getParamsUpdater('enableGpu')(value)}
            customLabel={
              <div className={classes.customLabel}>
                <Text variant="caption1" className={classes.customLabelTitle}>
                  GPU
                  <HintTooltip message="Enable GPU support for this cluster to allow GPU passthrough, or vGPU-based virtual machines. This must be turned on before GPU configuration or assignment." />
                </Text>
                <Text variant="body2">Enable running GPU workloads on the cluster.</Text>
              </div>
            }
          />
        </div>
        {params.enableGpu && (
          <div className={classes.gpuFields}>
            <Text variant="subtitle2">GPU Mode</Text>
            <Text variant="body2">
              Choose the type of GPU support you want this flavor to provide. Use <b>Passthrough</b>{' '}
              to assign full physical GPUs, or <b>vGPU</b> to use virtual GPU slices.
            </Text>
            <div className={classes.gpuCards}>
              {gpuModes.map(({ label, value }) => (
                <SelectableCard
                  id={value}
                  onClick={() => updateParams({ gpuType: value })}
                  active={params.gpuType === value}
                  withCustomBody
                  showRadioButton
                  className={classes.card}
                >
                  <Text variant="caption1">{label}</Text>
                </SelectableCard>
              ))}
            </div>
          </div>
        )}
        <div className={classes.toggleSwitch}>
          <ToggleSwitchField
            id="shared"
            label="Shared With All Tenants"
            value={params.shared}
            onChange={getParamsUpdater('shared')}
            customLabel={
              <div className={classes.customLabel}>
                <Text variant="caption1" className={classes.customLabelTitle}>
                  Shared With All Tenants
                  <HintTooltip message="When off, only the selected tenants can see and schedule VMs on this cluster. Shared clusters remain usable by every tenant." />
                </Text>
                <Text variant="body2">
                  Choose whether this cluster is shared with every tenant across all domains, or
                  dedicated to specific tenants only.
                </Text>
              </div>
            }
          />
        </div>
        {!params.shared && (
          <>
            {submitAttempted && params.tenantIds.length === 0 && (
              <div className={classes.tenantAccessField}>
                <ErrorMessage>Select at least one tenant, or turn on Shared.</ErrorMessage>
              </div>
            )}
            <ClusterAccessField
              className={classes.tenantAccessField}
              value={params.tenantIds}
              onChange={(ids) => updateParams({ tenantIds: ids })}
            />
          </>
        )}
      </FormFieldSection>
    </ModalForm>
  )
}
